<template>
  <div class="home">

    <!-- ═══════════════════════════════════════
         HERO
    ════════════════════════════════════════ -->
    <section class="hero">
      <!-- Playful decorations -->
      <div class="hero-deco hero-deco--star1" aria-hidden="true">✦</div>
      <div class="hero-deco hero-deco--star2" aria-hidden="true">✦</div>
      <div class="hero-deco hero-deco--star3" aria-hidden="true">✦</div>
      <div class="hero-deco hero-deco--star4" aria-hidden="true">✦</div>
      <div class="hero-deco hero-deco--star5" aria-hidden="true">✦</div>
      <div class="hero-deco hero-deco--star6" aria-hidden="true">✦</div>
      <div class="hero-deco hero-deco--star7" aria-hidden="true">✦</div>
      <div class="hero-deco hero-deco--star8" aria-hidden="true">✦</div>
      <div class="hero-deco hero-deco--star9" aria-hidden="true">✦</div>
      <div class="hero-deco hero-deco--star10" aria-hidden="true">✦</div>
      <div class="hero-deco hero-deco--star8" aria-hidden="true">✦</div>
      <div class="hero-deco hero-deco--star9" aria-hidden="true">✦</div>
      <div class="hero-deco hero-deco--star10" aria-hidden="true">✦</div>
      <div class="hero-deco hero-deco--blob1" aria-hidden="true"></div>
      <div class="hero-deco hero-deco--blob2" aria-hidden="true"></div>
      <div class="hero-deco hero-deco--dot1" aria-hidden="true"></div>
      <div class="hero-deco hero-deco--dot2" aria-hidden="true"></div>
      <div class="hero-bg-text" aria-hidden="true">NEXASCALE</div>
      <div class="hero-inner">
        <div class="hero-content">
          <br />
          <div class="hero-floating-pill" aria-hidden="true">
            <span class="hero-pill-avatars">
              <span class="hero-pill-av">💛</span>
              <span class="hero-pill-av">🌍</span>
              <span class="hero-pill-av">🚀</span>
            </span>
            <span class="hero-pill-text">11,000+ people building together</span>
          </div>
          <br /><br />
          <div class="hero-label">
            <span class="hero-label-dot"></span>
            A global tech community
          </div>
          <h1 class="hero-h1">
            Start here.<br/>
            <span class="hero-h1-accent">Scale <span class="hero-h1-glow">everywhere.</span></span>
          </h1>
          <p class="hero-sub">
            NexaScale is a nonprofit community of 11,000+ builders, learners, and tech professionals 
            growing their careers through real projects, mentorship, and radical access.
          </p>
          <div class="hero-actions">
            <router-link class="hero-btn-primary" to="/get-involved">Join the community</router-link>
            <router-link class="hero-btn-ghost" to="/programs">See our programs</router-link>
          </div>
          <div class="hero-stats">
            <div class="hero-stat">
              <span class="hero-stat-num">11K+</span>
              <span class="hero-stat-label">Members</span>
            </div>
            <div class="hero-stat-divider"></div>
            <div class="hero-stat">
              <span class="hero-stat-num">6</span>
              <span class="hero-stat-label">Continents</span>
            </div>
            <div class="hero-stat-divider"></div>
            <div class="hero-stat">
              <span class="hero-stat-num">3+</span>
              <span class="hero-stat-label">Years running</span>
            </div>
          </div>
        </div>
        <div class="hero-image-wrap">
          <div class="hero-image-frame">
            <img src="../assets/images/header-min.jpg" alt="NexaScale community" class="hero-img" />
            <div class="hero-image-badge">
              <span class="badge-icon">🚀</span>
              <div>
                <p class="badge-title">Open to everyone</p>
                <p class="badge-sub">Free to join</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="hero-scroll-hint" aria-hidden="true">
        <span class="scroll-line"></span>
        <span class="scroll-label">Scroll</span>
      </div>
    </section>

    <!-- ═══════════════════════════════════════
         MISSION MARQUEE
    ════════════════════════════════════════ -->
    <div class="marquee-strip">
      <div class="marquee-track">
        <span>Build with people</span>
        <span class="marquee-dot">◆</span>
        <span>Scale your career</span>
        <span class="marquee-dot">◆</span>
        <span>Real projects</span>
        <span class="marquee-dot">◆</span>
        <span>Open access</span>
        <span class="marquee-dot">◆</span>
        <span>11,000+ members</span>
        <span class="marquee-dot">◆</span>
        <span>Build with people</span>
        <span class="marquee-dot">◆</span>
        <span>Scale your career</span>
        <span class="marquee-dot">◆</span>
        <span>Real projects</span>
        <span class="marquee-dot">◆</span>
        <span>Open access</span>
        <span class="marquee-dot">◆</span>
        <span>11,000+ members</span>
        <span class="marquee-dot">◆</span>
      </div>
    </div>

    <!-- ═══════════════════════════════════════
         MISSION
    ════════════════════════════════════════ -->
    <section class="mission-section">
      <span class="section-spark section-spark--1" aria-hidden="true">✦</span>
      <span class="section-spark section-spark--2" aria-hidden="true">✦</span>
      <div class="mission-inner">
        <p class="section-eyebrow">Why we exist</p>
        <h2 class="mission-h2">
          To build a community where<br/>
          people can <em>connect and grow</em><br/>
          with each other.
        </h2>
        <div class="mission-footer">
          <p class="mission-body">
            We understand the barriers entry-level tech professionals face. No experience, 
            no network, no access. NexaScale closes every one of those gaps — through 
            projects, programs, and a community that actually shows up.
          </p>
          <router-link class="link-cta" to="/donate">Support our work →</router-link>
        </div>
      </div>
    </section>

    <!-- ═══════════════════════════════════════
         PROGRAMS
    ════════════════════════════════════════ -->
    <section class="programs-section">
      <div class="programs-section-header">
        <p class="section-eyebrow">What we do</p>
        <h2 class="programs-h2">Programs that move people forward.</h2>
      </div>

      <div class="programs-list">

        <!-- 01 Apprenticeship -->
        <div class="program-item program-item--light">
          <div class="program-item-inner">
            <div class="program-item-left">
              <span class="prog-num">01</span>
              <div class="prog-meta">
                <p class="prog-tag">Industry &amp; Academic Partnerships</p>
                <h3 class="prog-title">NexaScale Apprenticeship</h3>
              </div>
            </div>
            <div class="program-item-body">
              <p class="prog-desc">
                Real work experience through structured apprenticeships with industry and academic partners — 
                including <strong>Miva University</strong>. Community members are placed in hands-on roles where 
                they build actual products and emerge genuinely hirable.
              </p>
              <ul class="prog-list">
                <li>Placed with partner organisations for real work experience</li>
                <li>Academic credit pathways through university partners</li>
                <li>Mock interviews and resume reviews before placement</li>
              </ul>
            </div>
            <div class="program-item-cta">
              <router-link class="prog-cta-btn" to="/get-involved">Apply now</router-link>
            </div>
          </div>
        </div>

        <!-- 02 Future STEM Stars -->
        <div class="program-item program-item--dark">
          <div class="program-item-inner">
            <div class="program-item-left">
              <span class="prog-num prog-num--light">02</span>
              <div class="prog-meta">
                <p class="prog-tag prog-tag--light">Girls in Tech · Nigeria</p>
                <h3 class="prog-title prog-title--light">Future STEM Stars</h3>
              </div>
            </div>
            <div class="program-item-body">
              <p class="prog-desc prog-desc--light">
                Structured AI and technology education delivered to girls in Nigerian public secondary schools. 
                We show up before they've been told tech isn't for them — building the skills, mentorship, 
                and community they need to own their futures.
              </p>
              <ul class="prog-list prog-list--light">
                <li>AI curriculum taught in public secondary schools across Nigeria</li>
                <li>1:1 mentorship with women working in tech</li>
                <li>A peer community for girls in STEM to grow together</li>
              </ul>
            </div>
            <div class="program-item-cta">
              <router-link class="prog-cta-btn prog-cta-btn--yellow" to="/get-involved">Get involved</router-link>
            </div>
          </div>
        </div>

        <!-- 03 Laptop Sharing -->
        <div class="program-item program-item--orange">
          <div class="program-item-inner">
            <div class="program-item-left">
              <span class="prog-num prog-num--ink">03</span>
              <div class="prog-meta">
                <p class="prog-tag prog-tag--ink">Access &amp; Equity</p>
                <h3 class="prog-title prog-title--ink">Laptop Sharing Initiative</h3>
              </div>
            </div>
            <div class="program-item-body">
              <p class="prog-desc prog-desc--ink">
                A laptop shouldn't be the reason someone can't break into tech. We collect, refurbish, 
                and redistribute devices to community members who need them — then pass them forward 
                once each recipient lands their footing.
              </p>
              <ul class="prog-list prog-list--ink">
                <li>Apply to receive a refurbished device</li>
                <li>Donate your old laptop to keep the cycle going</li>
                <li>Devices passed forward once recipients land their first role</li>
              </ul>
            </div>
            <div class="program-item-cta">
              <router-link class="prog-cta-btn prog-cta-btn--ink" to="/get-involved">Donate or apply</router-link>
            </div>
          </div>
        </div>

      </div>
      <div class="programs-footer">
        <router-link class="programs-see-all" to="/programs">See all our programs →</router-link>
      </div>
    </section>

    <!-- ═══════════════════════════════════════
         TESTIMONIALS
    ════════════════════════════════════════ -->
    <section class="testimonials-section">
      <span class="section-spark section-spark--3" aria-hidden="true">✦</span>
      <span class="section-spark section-spark--4" aria-hidden="true">✦</span>
      <div class="testimonials-inner">
        <p class="section-eyebrow">Community voices</p>
        <h2 class="testimonials-h2">Don't take our word for it.</h2>
        <div class="testimonials-grid">
          <div class="tcard">
            <p class="tcard-quote">"I was happy to get a scholarship to join Co.Lab. It was a great experience working with amazing people in a cross-functional team. Co.Lab helped me discover a new side of myself."</p>
            <div class="tcard-author">
              <div class="tcard-avatar">IJ</div>
              <div>
                <p class="tcard-name">Igboagu Ijeoma</p>
                <p class="tcard-role">NexaScale Member</p>
              </div>
            </div>
          </div>
          <div class="tcard tcard--featured">
            <p class="tcard-quote">"Wolfstation and Co.Lab helped me build more confidence — I am good enough to get hired by some of the best companies in the world with all the skills I learned."</p>
            <div class="tcard-author">
              <div class="tcard-avatar">EE</div>
              <div>
                <p class="tcard-name">Ehis Edemakhiota</p>
                <p class="tcard-role">NexaScale Member</p>
              </div>
            </div>
          </div>
          <div class="tcard">
            <p class="tcard-quote">"Joining NexaScale Study Groups has been one of the best decisions I made as a developer. I met great minds, gained diverse perspectives, and became a more well-rounded developer."</p>
            <div class="tcard-author">
              <div class="tcard-avatar">DN</div>
              <div>
                <p class="tcard-name">David Ndubuisi</p>
                <p class="tcard-role">NexaScale Member</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ═══════════════════════════════════════
         LABS
    ════════════════════════════════════════ -->
    <section class="labs-section">
      <div class="labs-inner">
        <div class="labs-text">
          <h2 class="labs-h2">NexaScale <span class="labs-accent">Labs</span></h2>
          <p class="labs-body">
            Labs is our product incubation arm — where community members stop learning 
            and start shipping. Real products, built for real people.
            Our first product is <strong>Kowaara</strong>, an AI-powered learning platform 
            designed for mobile-first learners across emerging markets.
          </p>
          <a href="https://labs.nexascale.org" target="_blank" class="labs-cta-btn">
            Explore Labs →
          </a>
        </div>
        <div class="labs-card">
          <p class="labs-card-label">First product from Labs</p>
          <h3 class="labs-card-name">Kowaara</h3>
          <p class="labs-card-desc">
            AI-powered learning that meets students where they are. Flashcards, ELI5 summaries, 
            mock quizzes, and more — built for Nigerian students on mobile.
          </p>
          <div class="labs-tags">
            <span class="labs-tag">LLM-powered</span>
            <span class="labs-tag">Mobile-first</span>
            <span class="labs-tag">Azure</span>
            <span class="labs-tag">Emerging markets</span>
          </div>
        </div>
      </div>
    </section>

    <!-- ═══════════════════════════════════════
         RESOURCE LIBRARY
    ════════════════════════════════════════ -->
    <section class="rl-section">
      <div class="rl-inner">
        <div class="rl-text">
          <p class="rl-label">Free · By women, for women</p>
          <h2 class="rl-h2">📚 NexaScale Resource Library ✨</h2>
          <p class="rl-body">
            A community-built library of tech resources created by women in NexaScale — 
            covering Cloud, AI, Cybersecurity, DevOps, career advice, and more. 
            Free to access. Free to contribute.
          </p>
          <a href="https://nexascale-resource-library.vercel.app" target="_blank" class="rl-link">
            Explore the library →
          </a>
        </div>
        <div class="rl-topics">
          <div class="rl-topic">Cloud Computing</div>
          <div class="rl-topic">Cyber Security</div>
          <div class="rl-topic">Data Science &amp; AI</div>
          <div class="rl-topic">DevOps &amp; Engineering</div>
          <div class="rl-topic">Mentorship &amp; Career</div>
          <div class="rl-topic">Job Hunting &amp; Interview Prep</div>
        </div>
      </div>
    </section>

    <!-- ═══════════════════════════════════════
         PARTNERS
    ════════════════════════════════════════ -->
    <section class="partners-section">
      <div class="partners-inner">
        <p class="section-eyebrow">Our Partners</p>
        <h2 class="partners-h2">Backed by organisations that believe in access 🧡</h2>
        <div class="partners-grid">
          <div class="partner-cell"><img class="partner-logo" src="../assets/images/partners/landmark.jpg" alt="" /></div>
          <div class="partner-cell"><img class="partner-logo" src="../assets/images/partners/moniepointlogo.png" alt="" /></div>
          <div class="partner-cell"><img class="partner-logo" src="../assets/images/partners/selar-logo.svg" alt="" /></div>
          <div class="partner-cell"><img class="partner-logo" src="../assets/images/partners/miva-blue-logo.png" alt="" /></div>
          <div class="partner-cell"><img class="partner-logo" src="../assets/images/partners/thenest.png" alt="" /></div>
          <div class="partner-cell"><img class="partner-logo" src="../assets/images/partners/csf4.png" alt="" /></div>
          <div class="partner-cell"><img class="partner-logo" src="../assets/images/partners/hervest.png" alt="" /></div>
          <div class="partner-cell"><img class="partner-logo" src="../assets/images/partners/wetechng.png" alt="" /></div>
          <div class="partner-cell"><img class="partner-logo" src="../assets/images/partners/deimos.png" alt="" /></div>
          <div class="partner-cell"><img class="partner-logo" src="../assets/images/partners/colab.png" alt="" /></div>
          <div class="partner-cell"><img class="partner-logo" src="../assets/images/partners/accrue.webp" alt="" /></div>
          <div class="partner-cell"><img class="partner-logo" src="../assets/images/partners/pipeops.png" alt="" /></div>
          <div class="partner-cell"><img class="partner-logo" src="../assets/images/partners/genesys.png" alt="" /></div>
          <div class="partner-cell"><img class="partner-logo" src="../assets/images/partners/women-tech.png" alt="" /></div>
          <div class="partner-cell"><img class="partner-logo" src="../assets/images/partners/mantahq.png" alt="" /></div>
          <div class="partner-cell"><img class="partner-logo" src="../assets/images/partners/devslam.jpg" alt="" /></div>
          <div class="partner-cell"><img class="partner-logo" src="../assets/images/partners/codematic.png" alt="" /></div>
          <div class="partner-cell"><img class="partner-logo" src="../assets/images/partners/gitex.png" alt="" /></div>
          <div class="partner-cell"><img class="partner-logo" src="../assets/images/partners/kd.jpeg" alt="" /></div>
          <div class="partner-cell"><img class="partner-logo" src="../assets/images/partners/alt-school.png" alt="" /></div>
          <div class="partner-cell"><img class="partner-logo" src="../assets/images/partners/owyla.png" alt="" /></div>
          <div class="partner-cell"><img class="partner-logo" src="../assets/images/partners/condia.png" alt="" /></div>
          <div class="partner-cell"><img class="partner-logo" src="../assets/images/partners/flutterbytes.jpg" alt="" /></div>
          <div class="partner-cell"><img class="partner-logo" src="../assets/images/partners/techcabal.png" alt="" /></div>
          <div class="partner-cell"><img class="partner-logo" src="../assets/images/partners/techsafe.jpeg" alt="" /></div>
          <div class="partner-cell"><img class="partner-logo" src="../assets/images/partners/thegrowthhub.jpg" alt="" /></div>
          <div class="partner-cell"><img class="partner-logo" src="../assets/images/partners/wtec.png" alt="" /></div>
          <div class="partner-cell"><img class="partner-logo" src="../assets/images/partners/wtech.png" alt="" /></div>
          <div class="partner-cell"><img class="partner-logo" src="../assets/images/partners/yellowlyfe.png" alt="" /></div>
          <div class="partner-cell"><img class="partner-logo" src="../assets/images/partners/alvative.jpeg" alt="" /></div>
        </div>
      </div>
    </section>

    <!-- ═══════════════════════════════════════
         FINAL CTA
    ════════════════════════════════════════ -->
    <section class="final-cta">
      <span class="section-spark section-spark--5 section-spark--dark" aria-hidden="true">✦</span>
      <span class="section-spark section-spark--6 section-spark--dark" aria-hidden="true">✦</span>
      <div class="final-cta-inner">
        <h2 class="final-cta-h2">Your career in tech<br/><span class="final-cta-accent final-cta-accent--pulse">starts here.</span></h2>
        <p class="final-cta-body">Join a community that shows up for you — programs, projects, and people who get it.</p>
        <router-link class="hero-btn-primary" to="/get-involved">Join NexaScale free →</router-link>
      </div>
    </section>

  </div>
</template>

<style scoped>
/* ════════════════════════════════════════
   RESET
════════════════════════════════════════ */
.home {
  min-height: var(--pageheight, 100vh);
  margin-top: 80px;
  text-align: left !important;
  overflow-x: hidden;
}
.home * { box-sizing: border-box; }
.home p, .home h1, .home h2, .home h3, .home ul { text-align: left; }

/* ════════════════════════════════════════
   SHARED
════════════════════════════════════════ */
.section-eyebrow {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #FFB812;
  margin-bottom: 14px;
  display: block;
}

/* ════════════════════════════════════════
   HERO
════════════════════════════════════════ */
.hero {
  background: #191616;
  min-height: 100vh;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 100px 7% 80px;
}
.hero-bg-text {
  position: absolute;
  bottom: -60px;
  left: -10px;
  font-size: clamp(120px, 18vw, 220px);
  font-weight: 900;
  letter-spacing: -0.04em;
  color: rgba(255,255,255,0.025);
  line-height: 1;
  user-select: none;
  pointer-events: none;
  white-space: nowrap;
}
.hero-inner {
  display: flex;
  align-items: center;
  gap: 60px;
  max-width: 1280px;
  width: 100%;
  margin: 0 auto;
  position: relative;
  z-index: 1;
}
.hero-content {
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

/* Floating pill */
.hero-floating-pill {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,184,18,0.25);
  backdrop-filter: blur(12px);
  border-radius: 99px;
  padding: 8px 16px 8px 12px;
  margin-bottom: 20px;
  animation: float 6s ease-in-out infinite;
  pointer-events: none;
  max-width: 100%;
}
@keyframes float {
  0%, 100% { transform: translateY(0px); }
  50%       { transform: translateY(-8px); }
}
.hero-pill-avatars {
  display: flex;
  gap: 2px;
  font-size: 0.85rem;
  flex-shrink: 0;
}
.hero-pill-text {
  font-size: 0.72rem;
  font-weight: 600;
  color: rgba(255,255,255,0.65);
  white-space: nowrap;
  letter-spacing: 0.02em;
}

.hero-label {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(255,255,255,0.45);
  margin-bottom: 28px;
}
.hero-label-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  margin-left: 5px;
  background: #FFB812;
  display: inline-block;
  flex-shrink: 0;
  animation: pulse 2s infinite;
}
@keyframes pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.5; transform: scale(1.4); }
}

/* Hero headline — overflow locked down */
.hero-h1 {
  font-size: clamp(2.4rem, 5.5vw, 5.8rem);
  font-weight: 800;
  line-height: 1.0;
  letter-spacing: -0.03em;
  color: #ffffff;
  margin-bottom: 24px;
  max-width: 100%;
  overflow: hidden;
  word-break: break-word;
  overflow-wrap: break-word;
}
.hero-h1-accent {
  background: linear-gradient(90deg, #FFB812 0%, #FF9914 60%, #FF6B00 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  display: block;
  max-width: 100%;
  overflow: hidden;
}
.hero-h1-glow {
  -webkit-text-fill-color: #ffffff;
  color: #ffffff;
  text-shadow: rgba(255,184,0,0.9) 0px 0px 7px, rgba(255,184,0,0.7) 0px 0px 20px;
  display: inline;
}

.hero-sub {
  font-size: 1.05rem;
  line-height: 1.75;
  color: rgba(255,255,255,0.5);
  max-width: 500px;
  margin-bottom: 40px;
}
.hero-actions {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 52px;
}
.hero-btn-primary {
  display: inline-block;
  background: #FFB812;
  color: #191616;
  font-weight: 700;
  font-size: 0.95rem;
  padding: 14px 32px;
  border-radius: 8px;
  text-decoration: none;
  transition: background 0.2s, transform 0.15s;
}
.hero-btn-primary:hover { background: #FF9914; transform: translateY(-2px); }
.hero-btn-ghost {
  display: inline-block;
  color: rgba(255,255,255,0.6);
  font-weight: 600;
  font-size: 0.95rem;
  padding: 14px 24px;
  border-radius: 8px;
  text-decoration: none;
  border: 1.5px solid rgba(255,255,255,0.15);
  transition: border-color 0.2s, color 0.2s;
}
.hero-btn-ghost:hover { border-color: rgba(255,255,255,0.4); color: #fff; }
.hero-stats { display: flex; align-items: center; gap: 24px; }
.hero-stat { display: flex; flex-direction: column; gap: 2px; }
.hero-stat-num { font-size: 1.6rem; font-weight: 800; color: #ffffff; letter-spacing: -0.02em; line-height: 1; }
.hero-stat-label { font-size: 0.72rem; font-weight: 500; color: rgba(255,255,255,0.35); letter-spacing: 0.05em; text-transform: uppercase; }
.hero-stat-divider { width: 1px; height: 36px; background: rgba(255,255,255,0.12); }

.hero-image-wrap { flex-shrink: 0; width: 420px; position: relative; }
.hero-image-frame { position: relative; border-radius: 20px; overflow: hidden; }
.hero-img { width: 100%; border-radius: 20px; display: block; border: 1px solid rgba(255,184,18,0.3); box-shadow: -16px -16px 0 0 rgba(255,184,18,0.15); }
.hero-image-badge {
  position: absolute; bottom: 16px; left: 16px;
  background: rgba(255,255,255,0.95); backdrop-filter: blur(8px);
  border-radius: 12px; padding: 12px 18px;
  display: flex; align-items: center; gap: 12px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.25);
}
.badge-icon { font-size: 1.4rem; }
.badge-title { font-size: 0.82rem; font-weight: 700; color: #191616; margin: 0; line-height: 1.2; }
.badge-sub { font-size: 0.7rem; color: #888; margin: 0; line-height: 1.2; }

.hero-scroll-hint {
  position: absolute; bottom: 36px; left: 7%;
  display: flex; align-items: center; gap: 12px;
  color: rgba(255,255,255,0.2); font-size: 0.68rem;
  letter-spacing: 0.12em; text-transform: uppercase; font-weight: 600;
}
.scroll-line { display: block; width: 36px; height: 1px; background: rgba(255,255,255,0.2); }

/* ── Hero decorations ── */
.hero-deco { position: absolute; pointer-events: none; z-index: 0; }
@keyframes twinkle {
  0%, 100% { opacity: 0.4; transform: scale(1) rotate(0deg); }
  50% { opacity: 1; transform: scale(1.3) rotate(20deg); }
}
.hero-deco--star1  { top: 18%; right: 12%; font-size: 1.1rem; color: #FFB812; opacity: 0.7; animation: twinkle 3s ease-in-out infinite; }
.hero-deco--star2  { top: 55%; right: 6%;  font-size: 0.7rem; color: #FF9914; opacity: 0.5; animation: twinkle 4s ease-in-out infinite 1s; }
.hero-deco--star3  { top: 30%; left: 52%; font-size: 0.55rem; color: #FFB812; opacity: 0.4; animation: twinkle 2.5s ease-in-out infinite 0.5s; }
.hero-deco--star4  { top: 72%; right: 28%; font-size: 0.5rem; color: #FFB812; opacity: 0.35; animation: twinkle 3.5s ease-in-out infinite 0.8s; }
.hero-deco--star5  { top: 12%; left: 42%; font-size: 0.65rem; color: #FF9914; opacity: 0.3; animation: twinkle 4.5s ease-in-out infinite 1.2s; }
.hero-deco--star6  { top: 85%; right: 18%; font-size: 0.45rem; color: #FFB812; opacity: 0.4; animation: twinkle 2.8s ease-in-out infinite 0.3s; }
.hero-deco--star7  { top: 48%; left: 38%; font-size: 0.4rem; color: #FF9914; opacity: 0.25; animation: twinkle 5s ease-in-out infinite 2s; }
.hero-deco--star8  { top: 8%;  right: 32%; font-size: 0.6rem; color: #FFB812; opacity: 0.3; animation: twinkle 3.8s ease-in-out infinite 1.6s; }
.hero-deco--star9  { top: 65%; left: 44%; font-size: 0.35rem; color: #FF9914; opacity: 0.2; animation: twinkle 4.2s ease-in-out infinite 0.9s; }
.hero-deco--star10 { top: 38%; right: 22%; font-size: 0.5rem; color: #FFB812; opacity: 0.3; animation: twinkle 3.2s ease-in-out infinite 1.8s; }
.hero-deco--blob1 { width: 180px; height: 180px; border-radius: 50%; background: rgba(255,184,18,0.07); top: -40px; right: 38%; filter: blur(40px); }
.hero-deco--blob2 { width: 120px; height: 120px; border-radius: 50%; background: rgba(255,107,0,0.06); bottom: 15%; right: 3%; filter: blur(30px); }
.hero-deco--dot1 { width: 5px; height: 5px; border-radius: 50%; background: #FFB812; opacity: 0.5; top: 42%; right: 15%; }
.hero-deco--dot2 { width: 3px; height: 3px; border-radius: 50%; background: #FF9914; opacity: 0.4; top: 25%; right: 9%; }

/* ════════════════════════════════════════
   MARQUEE
════════════════════════════════════════ */
.marquee-strip { background: #FFB812; overflow: hidden; padding: 14px 0; white-space: nowrap; }
.marquee-track {
  display: inline-flex; align-items: center; gap: 28px;
  animation: marquee 28s linear infinite;
  font-size: 0.82rem; font-weight: 700; letter-spacing: 0.08em;
  text-transform: uppercase; color: #191616; padding-right: 28px;
}
@keyframes marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }
.marquee-dot { color: rgba(25,22,22,0.35); font-size: 0.5rem; }

/* ════════════════════════════════════════
   MISSION
════════════════════════════════════════ */
.mission-section { padding: 100px 7%; background: #ffffff; position: relative; overflow: hidden; }
.mission-inner { max-width: 1280px; margin: 0 auto; }
.mission-h2 {
  font-size: clamp(2rem, 4vw, 3.2rem); font-weight: 800;
  letter-spacing: -0.02em; line-height: 1.15; color: #191616; margin-bottom: 48px;
}
.mission-h2 em {
  font-style: normal;
  background: linear-gradient(90deg, #FF9914, #FFB812);
  -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
}
.mission-footer { display: flex; align-items: flex-end; justify-content: space-between; gap: 48px; border-top: 1px solid #e8e4e0; padding-top: 36px; }
.mission-body { font-size: 1rem; line-height: 1.8; color: #666260; max-width: 520px; margin: 0; }
.link-cta { white-space: nowrap; font-size: 0.9rem; font-weight: 700; color: #191616; text-decoration: none; border-bottom: 2px solid #FFB812; padding-bottom: 2px; transition: color 0.2s; flex-shrink: 0; }
.link-cta:hover { color: #FFB812; }

/* ════════════════════════════════════════
   PROGRAMS
════════════════════════════════════════ */
.programs-section { background: #FAFAF7; }
.programs-section-header { padding: 80px 7% 0; max-width: 1280px; margin: 0 auto; }
.programs-h2 { font-size: clamp(2rem, 4vw, 3rem); font-weight: 800; letter-spacing: -0.02em; line-height: 1.1; color: #191616; margin-bottom: 60px; max-width: 100%; word-break: break-word; }
.programs-list { display: flex; flex-direction: column; }
.program-item { border-top: 1px solid #e0dcd8; }
.program-item--light  { background: #ffffff; }
.program-item--dark   { background: #191616; }
.program-item--orange { background: #FFFBEB; }

.program-item--dark .prog-num,
.program-item--dark .prog-tag,
.program-item--dark .prog-title,
.program-item--dark .prog-desc,
.program-item--dark .prog-desc strong,
.program-item--dark .prog-list li,
.program-item--dark .prog-list li::before { color: #ffffff !important; -webkit-text-fill-color: #ffffff !important; }
.program-item--dark .prog-num { color: #FFB812 !important; -webkit-text-fill-color: #FFB812 !important; }
.program-item--dark .prog-list li { color: rgba(255,255,255,0.7) !important; border-bottom-color: rgba(255,255,255,0.08); }
.program-item--dark .prog-list li::before { color: #FFB812 !important; -webkit-text-fill-color: #FFB812 !important; }
.program-item--dark .prog-desc { color: rgba(255,255,255,0.7) !important; }
.program-item--dark .prog-desc strong { color: #FFB812 !important; -webkit-text-fill-color: #FFB812 !important; }
.program-item--dark .prog-cta-btn { background: #FFB812 !important; color: #191616 !important; }

.program-item-inner { max-width: 1280px; margin: 0 auto; padding: 60px 7%; display: grid; grid-template-columns: 260px minmax(0,1fr) auto; gap: 48px; align-items: start; }
.program-item-left { display: flex; flex-direction: column; gap: 10px; }
.prog-num { font-size: 0.68rem; font-weight: 800; letter-spacing: 0.16em; color: #FFB812; display: block; }
.prog-num--light { color: #FFB812; }
.prog-num--ink   { color: #FF9914; }
.prog-tag { font-size: 0.68rem; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; color: #aaa8a4; margin: 0 0 6px; }
.prog-tag--light { color: rgba(255,255,255,0.35); }
.prog-tag--ink   { color: #998760; }
.prog-title { font-size: 1.4rem; font-weight: 800; letter-spacing: -0.02em; color: #191616; margin: 0; line-height: 1.2; word-break: break-word; }
.prog-title--light { color: #ffffff; }
.prog-title--ink   { color: #191616; }
.program-item-body { min-width: 0; }
.prog-desc { font-size: 0.96rem; line-height: 1.78; color: #555250; margin: 0 0 20px; }
.prog-desc--light { color: rgba(255,255,255,0.5); }
.prog-desc--ink   { color: #555250; }
.prog-desc strong { color: #191616; font-weight: 700; }
.prog-desc--light strong { color: #FFB812; }
.prog-list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; }
.prog-list li { font-size: 0.88rem; color: #666260; padding: 10px 0 10px 22px; border-bottom: 1px solid #ece8e4; position: relative; line-height: 1.5; }
.prog-list li::before { content: '→'; position: absolute; left: 0; color: #FF9914; font-size: 0.78rem; top: 11px; }
.prog-list--light li { color: rgba(255,255,255,0.45); border-bottom-color: rgba(255,255,255,0.08); }
.prog-list--light li::before { color: #FFB812; }
.prog-list--ink li { color: #555250; border-bottom-color: rgba(25,22,22,0.1); }
.prog-list--ink li::before { color: #FF9914; }
.program-item-cta { padding-top: 4px; flex-shrink: 0; }
.prog-cta-btn { display: inline-block; padding: 11px 22px; border-radius: 8px; font-size: 0.85rem; font-weight: 700; text-decoration: none; white-space: nowrap; background: #191616; color: #ffffff; transition: background 0.2s; }
.prog-cta-btn:hover { background: #333; }
.prog-cta-btn--yellow { background: #FFB812; color: #191616; }
.prog-cta-btn--yellow:hover { background: #FF9914; }
.prog-cta-btn--ink { background: #191616; color: #ffffff; }
.prog-cta-btn--ink:hover { background: #333; }
.programs-footer { max-width: 1280px; margin: 0 auto; padding: 32px 7% 72px; border-top: 1px solid #e0dcd8; }
.programs-see-all { font-size: 0.9rem; font-weight: 700; color: #191616; text-decoration: none; border-bottom: 2px solid #FFB812; padding-bottom: 2px; transition: color 0.2s; }
.programs-see-all:hover { color: #FFB812; }

/* ════════════════════════════════════════
   TESTIMONIALS
════════════════════════════════════════ */
.testimonials-section { padding: 100px 7%; background: #ffffff; position: relative; overflow: hidden; }
.testimonials-inner { max-width: 1280px; margin: 0 auto; }
.testimonials-h2 { font-size: clamp(1.8rem, 3vw, 2.6rem); font-weight: 800; letter-spacing: -0.02em; color: #191616; margin-bottom: 52px; word-break: break-word; }
.testimonials-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; }
.tcard { background: #FAFAF7; border: 1px solid #e8e4e0; border-radius: 16px; padding: 32px 28px; display: flex; flex-direction: column; justify-content: space-between; gap: 28px; }
.tcard--featured { background: #191616; border-color: #191616; }
.tcard-quote { font-size: 0.95rem; line-height: 1.75; color: #444; margin: 0; font-style: italic; }
.tcard--featured .tcard-quote { color: rgba(255,255,255,0.65); }
.tcard-author { display: flex; align-items: center; gap: 12px; }
.tcard-avatar { width: 40px; height: 40px; border-radius: 50%; background: linear-gradient(135deg, #FFB812, #FF9914); color: #191616; font-size: 0.75rem; font-weight: 800; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.tcard-name { font-size: 0.85rem; font-weight: 700; color: #191616; margin: 0; line-height: 1.3; }
.tcard--featured .tcard-name { color: #ffffff; }
.tcard-role { font-size: 0.72rem; color: #999; margin: 0; }
.tcard--featured .tcard-role { color: rgba(255,255,255,0.4); }

/* ════════════════════════════════════════
   LABS
════════════════════════════════════════ */
.labs-section { background: #191616; padding: 100px 7%; }
.labs-inner { max-width: 1280px; margin: 0 auto; display: grid; grid-template-columns: 1fr 1fr; gap: 80px; align-items: center; }
.labs-h2 { font-size: clamp(2rem, 3.5vw, 3rem); font-weight: 800; letter-spacing: -0.02em; color: #ffffff; line-height: 1.1; margin-bottom: 20px; word-break: break-word; }
.labs-accent { background: linear-gradient(90deg, #FFB812, #FF9914); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; }
.labs-body { font-size: 0.97rem; line-height: 1.8; color: rgba(255,255,255,0.7); margin-bottom: 32px; }
.labs-body strong { color: #ffffff; }
.labs-cta-btn { display: inline-block; background: #FFB812; color: #191616; font-weight: 700; font-size: 0.92rem; padding: 13px 28px; border-radius: 8px; text-decoration: none; transition: background 0.2s; }
.labs-cta-btn:hover { background: #FF9914; }
.labs-card { background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.1); border-top: 3px solid #FF9914; border-radius: 20px; padding: 44px 40px; }
.labs-card-label { font-size: 0.68rem; font-weight: 700; letter-spacing: 0.14em; text-transform: uppercase; color: #FFB812; margin-bottom: 12px; }
.labs-card-name { font-size: 2rem; font-weight: 800; color: #ffffff; margin-bottom: 14px; letter-spacing: -0.02em; }
.labs-card-desc { font-size: 0.9rem; line-height: 1.7; color: rgba(255,255,255,0.65); margin-bottom: 24px; }
.labs-tags { display: flex; flex-wrap: wrap; gap: 8px; }
.labs-tag { background: rgba(255,255,255,0.1); color: rgba(255,255,255,0.75); padding: 5px 12px; border-radius: 6px; font-size: 0.73rem; font-weight: 600; }

/* ════════════════════════════════════════
   RESOURCE LIBRARY
════════════════════════════════════════ */
.rl-section { background: #FFFBEB; padding: 80px 7%; border-top: 1px solid #e8e4e0; border-bottom: 1px solid #e8e4e0; }
.rl-inner { max-width: 1280px; margin: 0 auto; display: grid; grid-template-columns: 1fr 1fr; gap: 80px; align-items: center; }
.rl-label { font-size: 0.65rem; font-weight: 700; letter-spacing: 0.16em; text-transform: uppercase; color: #FFB812; margin-bottom: 12px; display: block; }
.rl-h2 { font-size: clamp(1.6rem, 3vw, 2.4rem); font-weight: 800; letter-spacing: -0.02em; line-height: 1.15; color: #191616; margin-bottom: 16px; word-break: break-word; }
.rl-body { font-size: 0.96rem; line-height: 1.8; color: #666260; margin-bottom: 28px; }
.rl-link { display: inline-block; font-size: 0.9rem; font-weight: 700; color: #191616; text-decoration: none; border-bottom: 2px solid #FFB812; padding-bottom: 2px; transition: color 0.2s; }
.rl-link:hover { color: #FF9914; }
.rl-topics { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
.rl-topic { background: #ffffff; border: 1px solid #e8e4e0; border-radius: 8px; padding: 14px 18px; font-size: 0.85rem; font-weight: 600; color: #191616; transition: border-color 0.2s; }
.rl-topic:hover { border-color: #FFB812; }

/* ════════════════════════════════════════
   PARTNERS
════════════════════════════════════════ */
.partners-section { padding: 100px 7%; background: #FFFFFF !important; }
.partners-inner { max-width: 1280px; margin: 0 auto; }
.partners-h2 { font-size: clamp(1.6rem, 3vw, 2.4rem); font-weight: 800; letter-spacing: -0.02em; color: #191616; margin-bottom: 60px; line-height: 1.2; word-break: break-word; }
.partners-grid { display: flex; flex-wrap: wrap; gap: 0; border-top: 1px solid #e0dcd8; border-left: 1px solid #e0dcd8; }
.partner-cell { width: 16.666%; border-right: 1px solid #e0dcd8; border-bottom: 1px solid #e0dcd8; display: flex; align-items: center; justify-content: center; padding: 20px 24px; min-height: 90px; }
.partner-logo { width: 100%; max-width: 130px; height: auto; max-height: 60px; object-fit: contain; filter: grayscale(1); opacity: 0.6; transition: filter 0.2s, opacity 0.2s; }
.partner-cell:hover .partner-logo { filter: grayscale(0); opacity: 1; }

/* ════════════════════════════════════════
   FINAL CTA
════════════════════════════════════════ */
.final-cta { background: #FFB812; padding: 100px 7%; text-align: center; position: relative; overflow: hidden; }
.final-cta-inner { max-width: 680px; margin: 0 auto; }
.final-cta-h2 { font-size: clamp(2.2rem, 5vw, 3.8rem); font-weight: 800; letter-spacing: -0.03em; line-height: 1.05; color: #191616; margin-bottom: 20px; text-align: center; word-break: break-word; }
.final-cta-accent { display: block; }
.final-cta-accent--pulse { animation: ctaPulse 4s ease-in-out infinite; }
@keyframes ctaPulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.8; } }
.final-cta-body { font-size: 1rem; line-height: 1.7; color: rgba(25,22,22,0.65); margin-bottom: 36px; text-align: center; }
.final-cta .hero-btn-primary { background: #191616; color: #FFB812; }
.final-cta .hero-btn-primary:hover { background: #333; }

/* ════════════════════════════════════════
   PAGE-WIDE SPARKLES
════════════════════════════════════════ */
.section-spark { position: absolute; pointer-events: none; font-size: 0.7rem; color: #FFB812; animation: twinkle 3.5s ease-in-out infinite; z-index: 0; }
.section-spark--dark { color: rgba(25,22,22,0.3); }
.section-spark--1 { top: 14%;  right: 6%;  opacity: 0.5; animation-duration: 4s; }
.section-spark--2 { bottom: 18%; left: 4%; opacity: 0.3; animation-duration: 5s; animation-delay: 1s; font-size: 0.45rem; }
.section-spark--3 { top: 8%;   right: 3%;  opacity: 0.4; animation-duration: 3.8s; animation-delay: 0.7s; }
.section-spark--4 { bottom: 12%; left: 2%; opacity: 0.3; animation-duration: 4.5s; animation-delay: 1.5s; font-size: 0.5rem; }
.section-spark--5 { top: 20%;  right: 8%;  opacity: 0.35; animation-duration: 4.2s; }
.section-spark--6 { bottom: 25%; left: 5%; opacity: 0.25; animation-duration: 5.5s; animation-delay: 2s; font-size: 0.45rem; }

/* ════════════════════════════════════════
   RESPONSIVE
════════════════════════════════════════ */
@media (max-width: 1024px) {
  .hero-inner { flex-direction: column; gap: 48px; }
  .hero-image-wrap { width: 100%; max-width: 480px; }
  .program-item-inner { grid-template-columns: 1fr; gap: 28px; }
  .program-item-left { flex-direction: row; align-items: center; gap: 20px; }
  .labs-inner { grid-template-columns: 1fr; gap: 48px; }
  .rl-inner { grid-template-columns: 1fr; gap: 40px; }
  .testimonials-grid { grid-template-columns: 1fr; gap: 16px; }
  .partner-cell { width: 25%; }
}
@media (max-width: 768px) {
  .home { margin-top: 60px; }
  .hero { padding: 80px 5% 60px; }
  .hero-stats { gap: 16px; }
  .mission-footer { flex-direction: column; align-items: flex-start; gap: 20px; }
  .partner-cell { width: 33.333%; }
  .programs-section-header { padding-left: 5%; padding-right: 5%; }
  .program-item-inner { padding: 44px 5%; }
  .mission-section, .testimonials-section, .labs-section,
  .partners-section, .final-cta { padding-left: 5%; padding-right: 5%; }
}
@media (max-width: 480px) {
  .hero-h1 { font-size: 2.4rem; }
  .hero-actions { flex-direction: column; align-items: flex-start; }
  .partner-cell { width: 50%; }
  .programs-section-header { padding-top: 60px; }
  .hero-pill-text { font-size: 0.65rem; }
}
</style>